import { useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import TableComponent from '../../components/layout/TableComponent';
import NoDataFound from '../../components/layout/NoDataFound';
import Loader from '../../components/layout/Loader';
import { formatDateUS } from '../../utils/commonFn';
import {
  ACTIVITY_DASHBOARD_HEADER,
  ACTIVITY_DASHBOARD_ROUTE,
  COMPANY_DETAILS_ROUTE,
  VIEW_CANDIDATE_ROUTE,
  VIEW_JOB_ORDER_ROUTE,
  VIEW_PLACEMENT_MANAGEMENT_ROUTE,
} from '../../utils/constant';
import { GET_ACTIVITY_DASHBOARD } from '../activity-dashboard/gql';
import {
  formatStageDisplay,
  getPlacementStatusLabel,
  getStageLabel,
  isPendingPlacementStatus,
} from '../../utils/helper';
import { ActivityDashboardRow } from '../agreements/interfaces';

const WIDGET_LIMIT = 5;

const ActivityDashboardWidget = (): JSX.Element => {
  const history = useHistory();

  const [fetchActivityDashboard, { loading, data }] = useLazyQuery(
    GET_ACTIVITY_DASHBOARD,
  );

  useEffect(() => {
    fetchActivityDashboard({
      variables: { payload: { page: 1, limit: WIDGET_LIMIT } },
    });
  }, [fetchActivityDashboard]);

  const items = data?.getActivityDashboard?.items ?? [];
  const totalCount = data?.getActivityDashboard?.count ?? 0;

  return (
    <>
      <div className="section-header p-0 d-flex align-items-center justify-content-between">
        <h5 className="mb-2">Activity Dashboard</h5>
        <Link to={ACTIVITY_DASHBOARD_ROUTE} className="link-tag">
          View all{totalCount > WIDGET_LIMIT ? ` (${totalCount})` : ''}
        </Link>
      </div>

      <div className="table-responsive">
        <TableComponent tableHeader={ACTIVITY_DASHBOARD_HEADER}>
          {loading && (
            <tbody>
              <tr>
                <td colSpan={5}>
                  <Loader loaderClass="sm-height " />
                </td>
              </tr>
            </tbody>
          )}

          {!loading && items.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={5} className="empty-table-td">
                  <NoDataFound text="No activity found" />
                </td>
              </tr>
            </tbody>
          )}

          {!loading && items.length > 0 && (
            <tbody>
              {items.map((row: ActivityDashboardRow) => (
                <tr key={row.id}>
                  <td
                    className="route-link text-truncate max-200"
                    onClick={() =>
                      history.push({
                        pathname: `${COMPANY_DETAILS_ROUTE}/${row.companyId}`,
                        state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                      })
                    }
                  >
                    <div>{row.companyName ?? '--'}</div>
                    <div className="text-muted small">
                      {row.jobOrder ?? '--'}
                    </div>
                  </td>
                  <td
                    className="route-link text-truncate max-200"
                    onClick={() =>
                      history.push({
                        pathname: `${VIEW_CANDIDATE_ROUTE}/${row.candidateId}`,
                        state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                      })
                    }
                  >
                    {row.candidateName ?? '--'}
                  </td>
                  <td>
                    <span className="text-capitalize badge badge-info">
                      {getStageLabel(row.latestStage)}
                    </span>
                    {row.latestStage?.toUpperCase() === 'SUBMITTED' &&
                      row.submissionStatus && (
                        <span
                          className={`text-capitalize badge ml-1 badge-${
                            row.submissionStatus === 'COMPLETED'
                              ? 'success'
                              : 'primary'
                          }`}
                        >
                          {row.submissionStatus === 'COMPLETED'
                            ? 'COMPLETED'
                            : 'PENDING'}
                        </span>
                      )}
                    {row.latestStage?.toUpperCase() === 'PLACED' &&
                      isPendingPlacementStatus(row.placementStatus) && (
                        <span className="text-capitalize badge ml-1 badge-warning">
                          {getPlacementStatusLabel(row.placementStatus!)}
                        </span>
                      )}
                  </td>
                  <td
                    className="route-link"
                    onClick={() => {
                      const stageUpper = row.latestStage?.toUpperCase();
                      if (stageUpper === 'PLACED' && row.placementId) {
                        history.push({
                          pathname: `${VIEW_PLACEMENT_MANAGEMENT_ROUTE}/${row.placementId}`,
                          state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                        });
                      } else if (row.jobOrderId) {
                        const statusValue =
                          stageUpper === 'SUBMITTED' ? 'Submitted' :
                          stageUpper === 'INTERVIEWING' ? 'Interviewed' :
                          stageUpper === 'OFFERED' ? 'Offered' : '';
                        const candidateName = encodeURIComponent(row.candidateName ?? '');
                        history.push({
                          pathname: `${VIEW_JOB_ORDER_ROUTE}/${row.jobOrderId}`,
                          search: `?activeTab=6${statusValue ? `&statusFilter=${statusValue}` : ''}${candidateName ? `&candidateName=${candidateName}` : ''}`,
                          state: { backUrl: ACTIVITY_DASHBOARD_ROUTE },
                        });
                      }
                    }}
                  >
                    {formatStageDisplay(row)}
                  </td>
                  <td>
                    {row.jobApplicantUpdatedAt
                      ? formatDateUS(row.jobApplicantUpdatedAt)
                      : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </TableComponent>
      </div>
    </>
  );
};

export default ActivityDashboardWidget;
